import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { ArrowUpRight, Calendar, ExternalLink, Film, Play } from "lucide-react";
import { useState } from "react";

type ArchiveCategory = "all" | "predications" | "enseignements" | "louange";

interface ArchiveItem {
  id: string;
  title: string;
  date: string;
  category: ArchiveCategory;
  categoryLabel: string;
  link: string;
  image: string;
  description: string;
  featured?: boolean;
}

const archiveItems: ArchiveItem[] = [
  {
    id: "serge-joseph",
    title: "Enseignements du Pasteur Serge Joseph",
    date: "Série Pastorale",
    category: "enseignements",
    categoryLabel: "Enseignements",
    link: "https://www.youtube.com/playlist?list=PL0nlVgXlDMZLZskitffr3Q56X-LyT9shS",
    image:
      "https://ik.imagekit.io/4apzi8lz0/egarizim/serge-joseph.jpg?updatedAt=1760896561467",
    description:
      "Recueil complet des exhortations et prédications thématiques dispensées par le Pasteur Serge Joseph pour l'édification de l'assemblée.",
    featured: true,
  },
  {
    id: "prestations",
    title: "Prestations Musicales & Cantiques",
    date: "Répertoire Continu",
    category: "louange",
    categoryLabel: "Louange & Adoration",
    link: "https://www.youtube.com/playlist?list=PL0nlVgXlDMZKcNNSQR8ToXE0q20P_8s0a",
    image: "https://i.ytimg.com/vi/MsUyJDRCxzM/hqdefault.jpg",
    description:
      "Les plus beaux chants d'adoration, interprétations chorales et prestations des groupes musicaux de Garizim enregistrés en sanctuaire.",
    featured: true,
  },
  {
    id: "predications-2020",
    title: "Prédications & Messages 2020",
    date: "Année 2020",
    category: "predications",
    categoryLabel: "Prédications",
    link: "https://www.youtube.com/playlist?list=PL0nlVgXlDMZJTBMEHEUAFdgJfToq7dJLv",
    image: "https://i.ytimg.com/vi/Gcxe1hSBcRA/hqdefault.jpg",
    description:
      "Messages d'espérance, de foi et de persévérance partagés durant l'année 2020 pour soutenir les familles face aux épreuves.",
  },
  {
    id: "predications-2016-part2",
    title: "Prédications 2016 — Volume 2",
    date: "Année 2016",
    category: "predications",
    categoryLabel: "Prédications",
    link: "https://www.youtube.com/playlist?list=PL0nlVgXlDMZIKdLEshyClLauJdHduv0fm",
    image: "https://i.ytimg.com/vi/AWfdldGVf7Y/hqdefault.jpg",
    description:
      "Deuxième volet des prédications de l'année 2016 axées sur la sanctification et la croissance spirituelle.",
  },
  {
    id: "predications-2016-part1",
    title: "Prédications 2016 — Volume 1",
    date: "Année 2016",
    category: "predications",
    categoryLabel: "Prédications",
    link: "https://www.youtube.com/playlist?list=PL0nlVgXlDMZKmw4t4ZdK0IwcUJXzapFWw",
    image: "https://i.ytimg.com/vi/mp6XX-0D6eA/hqdefault.jpg",
    description:
      "Première série des messages dominicaux de l'année 2016 prononcés au sanctuaire principal de Garizim.",
  },
  {
    id: "predications-2015",
    title: "Prédications & Cultes 2015",
    date: "Année 2015",
    category: "predications",
    categoryLabel: "Prédications",
    link: "https://www.youtube.com/playlist?list=PL0nlVgXlDMZKVFn8tDrE8qZ2K5g3MoCQh",
    image: "https://i.ytimg.com/vi/juxp5z_pGJQ/hqdefault.jpg",
    description:
      "Ensemble des cultes d'adoration et sermons marquants de l'année 2015 conservés dans les archives numériques de l'église.",
  },
];

export default function PageArchives() {
  const [activeCategory, setActiveCategory] = useState<ArchiveCategory>("all");

  const filteredItems = archiveItems.filter((item) => {
    if (activeCategory === "all") return true;
    return item.category === activeCategory;
  });

  return (
    <div className="min-h-screen bg-[#FDFCFB] text-slate-900 font-sans flex flex-col antialiased">
      <Header />

      <main className="flex-1">
        {/* HERO HEADER */}
        <section className="relative pt-20 pb-8 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 bg-slate-950 text-white overflow-hidden border-b border-slate-900">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/25 via-transparent to-transparent pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-church-gold-400 mb-3 sm:mb-6">
                <Film className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-church-gold-400" />
                <span>Médiathèque Numérique</span>
              </div>

              <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-2 sm:mb-6 leading-tight">
                Archives &amp; Prédications Vidéo
              </h1>

              <p className="text-xs sm:text-lg lg:text-xl text-slate-300 leading-relaxed font-normal line-clamp-3 sm:line-clamp-none">
                Retrouvez les messages qui ont marqué notre église. Accédez aux
                prédications dominicales, aux enseignements pastoraux et aux
                prestations de louange enregistrés au fil des années.
              </p>
            </div>
          </div>
        </section>

        {/* YOUTUBE CHANNEL SPOTLIGHT BANNER */}
        <section className="py-4 sm:py-8 bg-slate-100/90 border-b border-slate-200">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="rounded-2xl sm:rounded-3xl bg-white border border-slate-200 p-4 sm:p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6 shadow-sm">
              <div className="flex items-center gap-3.5 sm:gap-5 w-full md:w-auto">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-red-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                  <Play className="w-6 h-6 sm:w-8 sm:h-8 fill-current ml-0.5" />
                </div>
                <div>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-600 block">
                    Chaîne YouTube Officielle
                  </span>
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-slate-950 tracking-tight">
                    Église de Dieu Garizim Montréal
                  </h2>
                  <p className="hidden sm:block text-slate-600 text-xs sm:text-sm mt-0.5">
                    Abonnez-vous pour suivre nos cultes en direct chaque
                    dimanche et recevoir les nouvelles vidéos.
                  </p>
                </div>
              </div>

              <a
                href="https://www.youtube.com/@eglisegarizim1?sub_confirmation=1"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs sm:text-sm transition-all shadow-sm hover:shadow"
              >
                <span>S'abonner sur YouTube</span>
                <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ARCHIVE CONTENT */}
        <section className="py-8 sm:py-16 md:py-24 bg-[#FDFCFB]">
          <div className="container mx-auto px-4 sm:px-6">
            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 pb-4 sm:pb-8 border-b border-slate-200 mb-6 sm:mb-12">
              <div>
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-church-gold-600 block mb-0.5 sm:mb-1">
                  Catalogue Vidéo
                </span>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-950">
                  Playlists &amp; Séries Thématiques
                </h3>
              </div>

              <div className="overflow-x-auto pb-1 sm:pb-0">
                <div className="inline-flex p-1 rounded-xl sm:rounded-2xl bg-slate-100 border border-slate-200 shadow-inner whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setActiveCategory("all")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeCategory === "all"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Tout ({archiveItems.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveCategory("enseignements")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeCategory === "enseignements"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Enseignements
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveCategory("predications")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeCategory === "predications"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Prédications
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveCategory("louange")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeCategory === "louange"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Louange
                  </button>
                </div>
              </div>
            </div>

            {/* Video Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              {filteredItems.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl sm:rounded-3xl bg-white border border-slate-200/90 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1"
                >
                  <div>
                    {/* Thumbnail with Play Overlay */}
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block relative aspect-video bg-slate-950 overflow-hidden"
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                      />
                      <div className="absolute inset-0 bg-slate-950/25 group-hover:bg-slate-950/10 transition-colors flex items-center justify-center">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-red-600/90 group-hover:bg-red-600 text-white flex items-center justify-center shadow-xl group-hover:scale-110 transition-all">
                          <Play className="w-4 h-4 sm:w-6 sm:h-6 fill-current ml-0.5" />
                        </div>
                      </div>
                      <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-slate-900/100 backdrop-blur-md text-white border border-white/10">
                          {item.categoryLabel}
                        </span>
                      </div>
                    </a>

                    {/* Content */}
                    <div className="p-4 sm:p-6">
                      <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs font-semibold text-slate-500 mb-1.5 sm:mb-2">
                        <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-church-gold-600" />
                        <span>{item.date}</span>
                      </div>

                      <h4 className="text-base sm:text-xl font-bold text-slate-950 mb-2 sm:mb-3 tracking-tight group-hover:text-church-blue-800 transition-colors leading-snug">
                        {item.title}
                      </h4>

                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-2 sm:line-clamp-3">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Card Action */}
                  <div className="p-4 pt-0 sm:p-6 sm:pt-0">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl bg-slate-100 group-hover:bg-church-blue-800 text-slate-700 group-hover:text-white font-semibold text-xs sm:text-sm transition-all"
                    >
                      <span>Visionner sur YouTube</span>
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
